import { RouterModule, Route } from '@angular/router';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { NotFoundComponent } from './views/errors/not-found/not-found.component';
import { LoginComponent } from './views/pages/login/login.component';
import { ResetPasswordComponent } from './views/reset-password/reset-password.component';
import { VerifyEmailComponent } from './views/verify-email/verify-email.component';
import { LockComponent } from './views/pages/lock/lock.component';

import { UsersComponent } from './views/manage/users/users.component';
import { TeamsComponent } from './views/manage/teams/teams.component';
import { ContactsComponent } from './views/manage/contacts/contacts.component';
import { StorageComponent } from './views/manage/storage/storage.component';

import { TemplatesComponent } from "./views/workflows/templates/templates.component";
import { NewWorkflowTemplateComponent } from "./views/workflows/new-workflow-template/new-workflow-template.component";

import { AuthGuard } from 'app/helpers/auth.guard';
import { FileExplorerComponent } from './views/pages/file-explorer/file-explorer.component';
import { WFGeneralSettingsComponent } from './views/workflows/new-workflow-template/general-settings/general-settings.component';
import { WFMaterialsComponent } from './views/workflows/new-workflow-template/materials/materials.component';
import { WorkflowRulesComponent } from './views/workflows/new-workflow-template/workflow-rules/workflow-rules.component';
import { AddCloudStorageComponent } from './views/manage/storage/add-cloud-storage/add-cloud-storage.component';
import { AddWebDavStorageComponent } from './views/manage/storage/add-webdav-storage/add-webdav-storage.component';
import { AddAmazonStorageComponent } from './views/manage/storage/add-amazon-storage/add-amazon-storage.component';
import { AddSFTPStorageComponent } from './views/manage/storage/add-sftpstorage/add-sftpstorage.component';
import { AddFTPStorageComponent } from './views/manage/storage/add-ftpstorage/add-ftpstorage.component';
import { WorkflowStatusComponent } from './views/workflows/workflow-status/workflow-status.component';
import { WorkflowStatusInfoComponent } from './views/workflows/workflow-status/workflow-status-info/workflow-status-info.component';
import { MultiStepsTreeComponent } from './views/workflows/new-workflow-template/multi-steps-tree/multi-steps-tree.component';
import { CustomStepComponent } from './views/workflows/new-workflow-template/custom-step/custom-step.component';
import { CustomAutomatedActionsComponent } from './views/workflows/new-workflow-template/custom-automated-actions/custom-automated-actions.component';
import { FilesFoldersPickerComponent } from './shared/components/files-folders-picker/files-folders-picker.component';
import { WorkflowTemplatesComponent } from './views/workflows/workflow-execution/workflow-templates/workflow-templates.component';
import { AuthorizeComponent } from './views/accounts/authorize/authorize.component';
import { PoliciesPermissionsComponent } from './views/policies-permissions/policies-permissions.component';
import { AddCustomPolicyComponent } from './views/policies-permissions/add-custom-policy/add-custom-policy.component';
import { SearchComponent } from './views/pages/search/search.component';
import { SettingsComponent } from './views/settings/settings.component';
import { FormsExplorerComponent } from './views/forms-explorer/forms-explorer.component';
import { FormBuilderComponent } from './views/form-builder/form-builder.component';
import { FormDataComponent } from './views/form-data/form-data.component';
import { InitialSubmissionComponent } from './views/workflows/workflow-execution/initial-submission/initial-submission.component';
import { CompleteWorkflowTaskComponent } from './views/workflows/workflow-execution/complete-workflow-task/complete-workflow-task.component';
import { ExternalCompleteWorkflowTaskComponent } from './views/workflows/workflow-execution/external-complete-workflow-task/external-complete-workflow-task.component';
import { HomeComponent } from './views/home/home.component';
import { MyTasksComponent } from './views/my-tasks/my-tasks.component';
import { DashboardsComponent } from './views/manage/dashboards/dashboards.component';
import { FormComponent } from './views/gen/form/form.component';
import { ReportsComponent } from './views/manage/reports/reports.component';
import { AddEditReportComponent } from './views/manage/reports/add-edit-report/add-edit-report.component';
import { MetadataBuilderComponent } from './views/metadata-builder/metadata-builder.component';
import { WopiComponent } from './views/wopi/wopi.component';
import { PdfEditComponent } from './views/pdf-edit/pdf-edit.component';
import { DeletedFormsComponent } from './views/deleted-forms/deleted-forms.component';
import { DeletedFilesComponent } from './views/deleted-files/deleted-files.component';
import { AddEditConnectedAppComponent } from './views/connected-apps/add-edit-connected-app/add-edit-connected-app.component';
import { ConnectedAppRedirectComponent } from './views/connected-apps/connected-app-redirect/connected-app-redirect.component';
import { LogoutComponent } from './views/logout/logout.component';
import { MetadataComponent } from './views/manage/metadata/metadata.component';
import { DataImportsComponent } from './views/data-imports/data-imports.component';
import { SupportSsoComponent } from './views/accounts/support-sso/support-sso.component';
import { SubscriptionBillingComponent } from './views/subscription-billing/subscription-billing.component';
import { BillingSucceededComponent } from './views/billing-succeeded/billing-succeeded.component';
import { RegistrationProgressComponent } from './views/registration-progress/registration-progress.component';
import { SuperScriptsComponent } from './views/super-scripts/super-scripts.component';
import { ConnectedEmailRedirectComponent } from './views/connected-emails/connected-email-redirect/connected-email-redirect.component';
import { WorkflowTriggersComponent } from './views/workflow-triggers/workflow-triggers.component';

const routes: Route[] = [
  { path: '', pathMatch: 'full', redirectTo: 'home' },  
  {
    path: 'home', 
    canActivate: [AuthGuard], 
    component: HomeComponent
  },
  { path: 'accounts', canActivate: [AuthGuard], children:
    [
      { path: 'authorize', component: AuthorizeComponent },
      { path: 'support/sso', component: SupportSsoComponent }
    ]
  },

  { path: 'external', children: [
    { path: "workflows/complete-workflow-task/:stepId/exec/:execId/contact/:contactId/token/:token", component: ExternalCompleteWorkflowTaskComponent }
  ]},

  { path: 'registration-progress/:registrationid', component: RegistrationProgressComponent },

  { path: 'manage', canActivate: [AuthGuard], children:
    [
      { path: 'users', component: UsersComponent },
      { path: 'teams', component: TeamsComponent },
      { path: 'contacts', component: ContactsComponent },
      { path: 'dashboards', component: DashboardsComponent },
      { path: 'reports', component: ReportsComponent },
      { path: 'report/:reportid', component: AddEditReportComponent },
      { path: 'metadata', component: MetadataComponent },
      { path: 'storage', children:[
        { path:'',component: StorageComponent},
        {path: 'add-cloud/:clouddriver', component: AddCloudStorageComponent},
        {path: 'add-cloud/:clouddriver/:storageid', component: AddCloudStorageComponent},
        {path: 'add-webdav/:storageid', component: AddWebDavStorageComponent},
        {path: 'add-webdav', component: AddWebDavStorageComponent},
        {path: 'add-amazon/:storageid', component: AddAmazonStorageComponent},
        {path: 'add-amazon', component: AddAmazonStorageComponent},
        {path: 'add-sftp/:storageid', component: AddSFTPStorageComponent},
        {path: 'add-sftp', component: AddSFTPStorageComponent},
        {path: 'add-ftp/:storageid', component: AddFTPStorageComponent},
        {path: 'add-ftp', component: AddFTPStorageComponent}
      ]},
      { path: 'super-scripts', component: SuperScriptsComponent },
    ]
  },
  { path: 'connected-emails/redirect', component: ConnectedEmailRedirectComponent},
  { path: 'billing', component: SubscriptionBillingComponent, canActivate: [AuthGuard] },
  { path: 'billing-succeeded', component: BillingSucceededComponent, canActivate: [AuthGuard] },
  { path: 'billing-payment-method-succeeded', component: BillingSucceededComponent, canActivate: [AuthGuard] },
  { path: 'settings', component: SettingsComponent, canActivate: [AuthGuard] },
  { path: 'reset-password', component: ResetPasswordComponent },
  {path:'policies-permissions', canActivate: [AuthGuard], children:[
    {path:'', component: PoliciesPermissionsComponent},
    {path: 'policy/:policyId/:external', component: AddCustomPolicyComponent},
    {path: 'policy/:policyId', component: AddCustomPolicyComponent}
  ]},

  { path: 'verify-email', component: VerifyEmailComponent },
  { path: 'login', component: LoginComponent },
  { path: 'lock', component: LockComponent },

  { path: "my-tasks", component: MyTasksComponent, canActivate: [AuthGuard]},
  { path: "search", component: SearchComponent, canActivate: [AuthGuard]},
  {
    path: 'workflows', canActivate: [AuthGuard], children:
      [
        { path: 'templates', component: TemplatesComponent },
        { path: 'triggers', component: WorkflowTriggersComponent },
        {
          path: 'template/:workflowid', component: NewWorkflowTemplateComponent, children:
            [
              { path: 'general-settings', component: WFGeneralSettingsComponent },
              { path: 'materials', component: WFMaterialsComponent },
              { path: 'workflow-rules', component: WorkflowRulesComponent },
              { path: 'steps-tree', component: MultiStepsTreeComponent },
              { path: 'new-step', component: CustomStepComponent },
              { path: 'new-step-rules', component: CustomAutomatedActionsComponent },
              { path: 'step/:stepid/materials', component: CustomStepComponent },
              { path: 'step/:stepid/auto-actions', component: CustomAutomatedActionsComponent }
            ]
        },
        { path: "available", component: WorkflowTemplatesComponent },
        { path: "start-new/:id/initial-submission", component: InitialSubmissionComponent},
        { path: "complete-workflow-task/:stepId/exec/:execId", component: CompleteWorkflowTaskComponent },
        {
          path: 'status', children: [
            { path: '', component: WorkflowStatusComponent },
            { path: ':id', component: WorkflowStatusInfoComponent }
          ]
        }
      ]
  },
  { path: 'data-imports', canActivate: [AuthGuard], component: DataImportsComponent},
  { path: 'file-explorer/:id', pathMatch:'full', redirectTo: 'file-explorer/:id/'},
  { path: 'file-explorer/:id/:path', canActivate: [AuthGuard], component: FileExplorerComponent },
  { path: 'file-selector', canActivate: [AuthGuard], component: FilesFoldersPickerComponent },
  { path: 'deleted-files/:id', canActivate: [AuthGuard], component: DeletedFilesComponent },
  
  { path: 'deleted-forms', canActivate: [AuthGuard], component: DeletedFormsComponent },
  { path: 'forms-explorer', pathMatch:'full', redirectTo: 'forms-explorer/' },
  { path: 'forms-explorer/:path', canActivate: [AuthGuard], component: FormsExplorerComponent },
  { path: 'form-builder/:id', canActivate: [AuthGuard], component: FormBuilderComponent },
  { path: 'form-data/:id/spreadsheet-import/:spreadsheet_conversion_id', canActivate: [AuthGuard], component: FormDataComponent },
  { path: 'form-data/:id', canActivate: [AuthGuard], component: FormDataComponent },
  
  { path: 'gen/form', canActivate: [AuthGuard], component: FormComponent },

  { path: 'metadata-builder', canActivate: [AuthGuard], component: MetadataBuilderComponent },
  
  { path: 'wopi/:action/:extension/:fileID', canActivate: [AuthGuard], component: WopiComponent },
  
  { path: 'pdf/:storageId/:path', canActivate: [AuthGuard], component: PdfEditComponent },

  {path:'connected-apps', canActivate: [AuthGuard], children:[
    {path: 'app/:appId', pathMatch:'full', component: AddEditConnectedAppComponent},
    {path: 'app/:appId/auth/:authId/redirect/:globalOrUser', pathMatch:'full', component: ConnectedAppRedirectComponent}
  ]},

  { path: 'logout', canActivate: [AuthGuard], component: LogoutComponent },

  { path: '**', component: NotFoundComponent },  
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }

//export const AppRoutes: ModuleWithProviders = RouterModule.forRoot(routes);
